import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneIcon as PhoneSolidIcon } from "@heroicons/react/24/solid";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MicrophoneIcon,
  PhoneXMarkIcon,
  Squares2X2Icon,
  VideoCameraIcon,
  VideoCameraSlashIcon
} from "@heroicons/react/24/outline";
import { BcallLogo } from "./components/BcallLogo";
import { useCallTimer } from "./hooks/useCallTimer";
import { CallErrorCode, CallState, useSipClient } from "./hooks/useSipClient";
import { LanguageSelector, TranslationKey, useLocale } from "./i18n";
import { readCallCredentials } from "./rtc/callSession";
import { SetupDiagnostics } from "./components/SetupDiagnostics";

function errorTranslationKey(code: CallErrorCode | null): TranslationKey {
  if (code === "MICROPHONE_PERMISSION") return "errorMicrophonePermission";
  if (code === "NO_ANSWER") return "errorNoAnswer";
  if (code === "SESSION_EXPIRED") return "errorExpired";
  if (code === "RECIPIENT_UNAVAILABLE") return "errorRecipientUnavailable";
  return "errorConnectGeneric";
}

function currentProgressStep(callState: CallState): number {
  if (callState === "registered") return 1;
  if (callState === "calling") return 2;
  return 0;
}

export default function Dialer() {
  const navigate = useNavigate();
  const { dir, locale, t } = useLocale();
  const callerDisplayName = sessionStorage.getItem("callerName") || t("guest");
  const recipientName = sessionStorage.getItem("callRecipientName") || "Bcall";
  const returnPath = sessionStorage.getItem("callReturnPath") || "/";
  const {
    callState,
    errorCode,
    videoEnabled,
    remoteVideoEnabled,
    muted,
    connectAndCall,
    hangup,
    sendDTMF,
    toggleMute,
    toggleVideo
  } = useSipClient(callerDisplayName);
  const { duration, formattedDuration, isRunning, start, stop } = useCallTimer();
  const [dialPadOpen, setDialPadOpen] = useState(false);
  const [startupError, setStartupError] = useState<CallErrorCode | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    document.title = t("metaTitle");
    document.querySelector('meta[name="description"]')?.setAttribute("content", t("metaDescription"));
  }, [t]);

  const handleStartCall = useCallback(async () => {
    setStartupError(null);
    try {
      await connectAndCall(readCallCredentials());
    } catch (error) {
      console.error("Unable to start call", error);
      setStartupError("SESSION_EXPIRED");
    }
  }, [connectAndCall]);

  useEffect(() => {
    if (startedRef.current || callState !== "idle") return;
    startedRef.current = true;
    void handleStartCall();
  }, [callState, handleStartCall]);

  useEffect(() => {
    if (callState === "incall" && !isRunning) start();
    if ((callState === "ended" || callState === "error") && isRunning) stop();
  }, [callState, isRunning, start, stop]);

  const handleReturn = () => {
    const destination = new URL(returnPath, window.location.origin);
    destination.searchParams.set("lang", locale);
    navigate(`${destination.pathname}${destination.search}${destination.hash}`, { replace: true });
  };
  const isError = callState === "error" || Boolean(startupError);
  const isEnded = callState === "ended";
  const isActive = callState === "incall";
  const progressStep = currentProgressStep(callState);
  const recipientInitial = Array.from(recipientName.trim())[0]?.toUpperCase() || "B";

  const progressSteps = [t("prepareCall"), t("makeContact"), t("waitForAnswer")];
  const statusTitle = callState === "registered"
    ? t("makeContact")
    : callState === "calling"
      ? t("waitForAnswer")
      : isActive
        ? t("callActive")
        : t("prepareCall");
  const statusDescription = callState === "registered"
    ? t("checkingAvailability")
    : callState === "calling"
      ? t("callSentTo", { name: recipientName })
      : isActive
        ? t("connectionComplete")
        : t("creatingPrivateConnection");

  return (
    <main className="live-call-page" dir={dir}>
      <audio id="remoteAudio" autoPlay className="live-call-hidden-media" />

      <section className={`live-call-card${isEnded || isError ? " terminal" : ""}`} aria-live="polite">
        <header className="live-call-header">
          <div className="live-call-brand">
            <BcallLogo size={30} />
            <span>Bcall</span>
          </div>
          <div className="live-call-header-actions"><LanguageSelector /><span className="live-call-secure"><i aria-hidden="true" />{t("secureCall")}</span></div>
        </header>

        {isEnded ? (
          <div className="call-terminal-content">
            <div className="call-terminal-icon success"><CheckCircleIcon aria-hidden="true" /></div>
            <p className="call-terminal-kicker">{t("callWith")} <strong dir="auto">{recipientName}</strong></p>
            <h1>{t("callEnded")}</h1>
            {duration > 0 && (
              <div className="call-duration-summary">
                <span>{t("callDuration")}</span>
                <strong dir="ltr">{formattedDuration}</strong>
              </div>
            )}
            <p className="call-terminal-message">{t("thankYou")}</p>
            <button type="button" className="call-return-button" onClick={handleReturn}>
              <ArrowRightIcon aria-hidden="true" />
              <span>{t("returnToProfile", { name: recipientName })}</span>
            </button>
          </div>
        ) : isError ? (
          <div className="call-terminal-content">
            <div className="call-terminal-icon error"><ExclamationTriangleIcon aria-hidden="true" /></div>
            <p className="call-terminal-kicker">{t("callWith")} <strong dir="auto">{recipientName}</strong></p>
            <h1>{t("connectionFailed")}</h1>
            <p className="call-terminal-message" role="alert">
              {t(errorTranslationKey(startupError || errorCode))}
            </p>
            <button type="button" className="call-return-button" onClick={handleReturn}>
              <ArrowRightIcon aria-hidden="true" />
              <span>{t("returnToProfile", { name: recipientName })}</span>
            </button>
          </div>
        ) : (
          <>
            <div className="live-call-recipient">
              <div className={`live-call-avatar${isActive ? " connected" : ""}`} aria-hidden="true">
                <span dir="auto">{recipientInitial}</span>
                {!isActive && <i className="live-call-avatar-pulse" />}
              </div>
              <p>{t("callWith")}</p>
              <h1 dir="auto">{recipientName}</h1>
            </div>

            <div className={`live-call-video${videoEnabled || remoteVideoEnabled ? "" : " hidden"}`} aria-hidden={!videoEnabled && !remoteVideoEnabled}>
              <video id="remoteVideo" autoPlay playsInline muted className={remoteVideoEnabled ? "visible" : ""} />
              {!remoteVideoEnabled && <span>{t("remoteVideoInactive")}</span>}
              <video id="localVideo" autoPlay playsInline muted className={videoEnabled ? "local visible" : "local"} />
            </div>

            <div className={`live-call-status${isActive ? " active" : ""}`}>
              <span className="live-call-status-dot" aria-hidden="true" />
              <div>
                <strong>{statusTitle}</strong>
                <span>{statusDescription}</span>
              </div>
            </div>

            {isActive ? (
              <div className="live-call-timer" aria-label={`${t("callDuration")} ${formattedDuration}`}>
                <strong dir="ltr">{formattedDuration}</strong>
                <span>{t("callDuration")}</span>
              </div>
            ) : (
              <ol className="call-progress" aria-label={t("connectionProgress")}>
                {progressSteps.map((step, index) => (
                  <li key={step} className={index < progressStep ? "complete" : index === progressStep ? "current" : ""}>
                    <span aria-hidden="true">{index < progressStep ? "✓" : index + 1}</span>
                    <small>{step}</small>
                  </li>
                ))}
              </ol>
            )}

            {errorCode && isActive && <div className="live-call-inline-error" role="alert">{t(errorTranslationKey(errorCode))}</div>}

            {isActive && (
              <div className="call-controls" aria-label={t("callControls")}>
                <button type="button" className={muted ? "active" : ""} onClick={toggleMute} aria-pressed={muted}>
                  <MicrophoneIcon aria-hidden="true" />
                  <span>{muted ? t("unmute") : t("mute")}</span>
                </button>
                <button type="button" className={videoEnabled ? "active" : ""} onClick={toggleVideo} aria-pressed={videoEnabled}>
                  {videoEnabled ? <VideoCameraIcon aria-hidden="true" /> : <VideoCameraSlashIcon aria-hidden="true" />}
                  <span>{videoEnabled ? t("disableVideo") : t("video")}</span>
                </button>
                <button type="button" className={dialPadOpen ? "active" : ""} onClick={() => setDialPadOpen(value => !value)} aria-pressed={dialPadOpen}>
                  <Squares2X2Icon aria-hidden="true" />
                  <span>{t("keypad")}</span>
                </button>
              </div>
            )}

            {isActive && dialPadOpen && (
              <div className="call-keypad" dir="ltr" aria-label={t("keypadLabel")}>
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map(digit => (
                  <button type="button" key={digit} onClick={() => sendDTMF(digit)}>{digit}</button>
                ))}
              </div>
            )}

            <button type="button" className={`call-end-button${isActive ? " active" : ""}`} onClick={hangup}>
              {isActive ? <PhoneXMarkIcon aria-hidden="true" /> : <PhoneSolidIcon aria-hidden="true" />}
              <span>{isActive ? t("endCall") : t("cancel")}</span>
            </button>
          </>
        )}

        <footer className="live-call-footer">{t("privateBrowserConnection")}</footer>
      </section>
      <SetupDiagnostics />
    </main>
  );
}
